import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { PlxsToast } from "@plexlogic/plexus-ui";

interface Notification {
  id: string;
  code: string; // Error code to display as main message
  errorMessage: string; // Detailed error message to display as supportingCopy
  tone: "urgent" | "caution" | "positive"; // Flexible tone options
  isVisible: boolean; // Control toast visibility for transitions
}

interface NotificationContextType {
  showNotification: (
    code: string,
    errorMessage: string,
    tone?: "urgent" | "caution" | "positive"
  ) => void; // Flexible tone, defaults to urgent
  clearNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const showNotification = useCallback(
    (
      code: string,
      errorMessage: string,
      tone: "urgent" | "caution" | "positive" = "urgent"
    ) => {
      const id = `notification-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const notification: Notification = {
        id,
        code,
        errorMessage,
        tone, // Use provided tone, defaults to urgent
        isVisible: true, // Initially visible for toast transition
      }; // Debug logging in development
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        // Adding notification
      }

      setNotifications((prev) => [...prev, notification]);

      // Auto-dismiss after 5 seconds (PlxsToast has built-in auto-dismiss, but we manage removal)
      const timeoutId = setTimeout(() => {
        // First hide the toast (triggers transition out)
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isVisible: false } : n))
        );

        // Then remove it after transition completes (500ms)
        setTimeout(() => {
          setNotifications((prev) => prev.filter((n) => n.id !== id));
        }, 500);

        timeoutsRef.current.delete(id);
      }, 5000);

      // Store timeout for cleanup
      timeoutsRef.current.set(id, timeoutId);
    },
    []
  );

  const clearNotification = useCallback((id: string) => {
    // Clear the timeout if it exists
    const timeoutId = timeoutsRef.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutsRef.current.delete(id);
    }

    // Hide toast first (triggers transition out)
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isVisible: false } : n))
    );

    // Remove notification after transition completes
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 500);
  }, []);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      timeouts.clear();
    };
  }, []);

  return (
    <NotificationContext.Provider
      value={{ showNotification, clearNotification }}
    >
      {children}

      {/* Render toast notifications */}
      {notifications.map((notification) => (
        <PlxsToast
          key={notification.id}
          icon={
            notification.tone === "positive"
              ? "check"
              : notification.tone === "urgent"
              ? "warning"
              : "help"
          }
          dataTestId={`notification-toast-${notification.id}`}
          isVisible={notification.isVisible}
          onDismiss={() => clearNotification(notification.id)}
          tone={notification.tone}
          isAutoDismiss={true}
          autoDismissVisibleDuration={5000}
          canManuallyDismiss={true}
        >
          <strong>{notification.code}</strong>
          {notification.errorMessage && (
            <>
              <br />
              {notification.errorMessage}
            </>
          )}
        </PlxsToast>
      ))}
    </NotificationContext.Provider>
  );
};
